// Cash's position (cash where-we-stand Part 1, DD-041, DD-054; on jobs, finance consolidation S9): what today is made
// of, then its breakdowns by client, person and expense. Clients: what each owes for work done to today (still due on
// its sent invoices, and the work done on its jobs not sent yet, a paused job on neither side) and what the team is
// owed on it, an overdue invoice flagged; a row opens the client's drawer, its invoices (DD-051). People: each person's
// claims on work done to today, owed now or not, not yet paid out on a team invoice, by client in its note, with what
// was paid out ahead of the work shown apart under its own words, never the Team tab's Paid ahead, and never counted
// (TD-081); a row opens the person's drawer, their team invoices, never Pay or a statement. Costs: each expense run up
// to today less paid, opening its expense or its bill; a repeating bill is paid on its date and runs up over its
// period, so those paid ahead fold into one last muted row. A part below zero flips its words and shows its size, so no
// figure reads as a minus behind a minus (decision 16). Flat panels, divided rows, and on a phone a row keeps its name
// and one amount (DD-055). Every figure, totals included, is the server's (TD-102): the screen only formats them.
import type { ReactNode } from "react";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { cn } from "@/utils/cn";
import { formatMoney } from "@/utils/currency";
import { TotalsFooter, type TotalItem } from "../components/TotalsFooter";
import { JOB_INVOICE_STATES } from "../clientPage/lineJobText";
import { StatusPill } from "../clientPage/StatusBadge";
import type { ParamPatch } from "../hooks/useFinanceParams";
import type { AgencyCashFigure, CashPosition, PositionClient, PositionCost, PositionPerson } from "../types";
import { dateLabel } from "../utils/format";
import { clientName } from "../utils/names";
import { COMPOSITION, costNote, costOpens, opensPatch } from "./cashText";

const MONEY = "w-24 shrink-0 text-right text-sm";
/** A column a phone leaves out (DD-055). */
const WIDE = "hidden sm:block";
const ROW = "w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50";

/** The panel's column names, over the rows' money columns: the first stays on a phone. */
function Columns({ first, wide = [] }: { first: string; wide?: string[] }) {
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      <span className="min-w-0 flex-1" />
      <span className={cn(MONEY, "text-xs")}>{first}</span>
      {wide.map((label) => (
        <span key={label} className={cn(MONEY, WIDE, "text-xs")}>
          {label}
        </span>
      ))}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-6 text-sm text-gray-500">{children}</p>;
}

/** What a client or job owes: below zero it was paid before its work was done, so it reads paid ahead at its size, never
 * red (decision 16, DD-054). */
function OweCell({ amountMinor }: { amountMinor: number }) {
  return (
    <span className={MONEY}>
      <MoneyText amountMinor={Math.abs(amountMinor)} />
      {amountMinor < 0 && <span className="block text-xs text-gray-400">paid ahead</span>}
    </span>
  );
}

interface Part {
  sign: string;
  label: string;
  note?: string;
  amountMinor: number;
  balance?: boolean;
  strong?: boolean;
}

/** A part of today: below zero it flips its words and its sign and shows its size (decision 16). */
function flipped(amountMinor: number, sign: string, label: string, ahead: string, flip: string): Pick<Part, "sign" | "label" | "amountMinor"> {
  return amountMinor < 0 ? { sign: flip, label: ahead, amountMinor: -amountMinor } : { sign, label, amountMinor };
}

/** A footer's total in the composition's words, flipped below zero as its part is. */
function total(amountMinor: number, label: string, ahead: string): TotalItem {
  return amountMinor < 0 ? { label: ahead, amountMinor: -amountMinor } : { label, amountMinor };
}

/**
 * Today is made of (DD-054): the bank, what clients owe for work done to today sent or not, less the team and the costs,
 * equal to where we stand. Without the agency's cash (A4) neither the bank nor where we stand shows.
 */
export function TodayMadeOf({ position, agencyCash }: { position: CashPosition; agencyCash: AgencyCashFigure | null }) {
  const parts: Part[] = [
    ...(position.cashMinor === null
      ? []
      : [
          {
            sign: "",
            label: COMPOSITION.cash,
            note: agencyCash ? `counted from the agency's cash on ${dateLabel(agencyCash.asOf)}` : undefined,
            amountMinor: position.cashMinor,
            balance: true,
          },
        ]),
    { ...flipped(position.clientsOweMinor, "+", COMPOSITION.clients, COMPOSITION.clientsAhead, "−"), note: COMPOSITION.clientsNote },
    { sign: "−", label: COMPOSITION.team, note: COMPOSITION.teamNote, amountMinor: position.teamMinor },
    flipped(position.costsMinor, "−", COMPOSITION.costs, COMPOSITION.costsAhead, "+"),
    ...(position.positionMinor === null
      ? []
      : [{ sign: "=", label: COMPOSITION.total, amountMinor: position.positionMinor, balance: true, strong: true }]),
  ];
  return (
    <WorkspacePanel title={COMPOSITION.title} collapsed={false} bodyClassName="p-0">
      <ul aria-label={COMPOSITION.title} className="divide-y divide-gray-100">
        {parts.map((part) => (
          <li key={part.label} className="flex items-center gap-3 px-4 py-2.5">
            <span className="w-4 shrink-0 text-gray-400">{part.sign}</span>
            <span className="min-w-0 flex-1">
              <span className={cn("block text-sm text-gray-900", part.strong && "font-semibold")}>{part.label}</span>
              {part.note && <span className="block text-xs text-gray-500">{part.note}</span>}
            </span>
            <MoneyText amountMinor={part.amountMinor} balance={part.balance} strong={part.strong} className="w-28 shrink-0 text-right text-sm" />
          </li>
        ))}
      </ul>
    </WorkspacePanel>
  );
}

function ClientRow({ client, onOpen }: { client: PositionClient; onOpen: (patch: ParamPatch) => void }) {
  return (
    <li>
      <button type="button" onClick={() => onOpen(opensPatch({ kind: "client", clientId: client.clientId }))} className={ROW}>
        <span className="min-w-0 flex-1 flex items-center gap-2">
          <span className="text-sm text-gray-900 truncate">{clientName(client.clientName, client.ownClient)}</span>
          {client.overdueMinor > 0 && <StatusPill look={JOB_INVOICE_STATES.overdue} />}
        </span>
        <OweCell amountMinor={client.oweMinor} />
        <MoneyText amountMinor={client.teamMinor} className={cn(MONEY, WIDE)} />
      </button>
    </li>
  );
}

function PersonRow({ person, onOpen }: { person: PositionPerson; onOpen: (patch: ParamPatch) => void }) {
  const byClient = person.clients.map((line) => `${clientName(line.clientName, line.ownClient)} ${formatMoney(line.amountMinor, "EUR")}`).join(" · ");
  return (
    <li>
      <button type="button" onClick={() => onOpen(opensPatch({ kind: "person", personId: person.personId }))} className={ROW}>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-gray-900 truncate">{person.personName}</span>
          {byClient && <span className="block text-xs text-gray-500 truncate">{byClient}</span>}
          {person.aheadMinor > 0 && <span className="block text-xs text-gray-400">{`${COMPOSITION.teamAhead} ${formatMoney(person.aheadMinor, "EUR")}`}</span>}
        </span>
        <MoneyText amountMinor={person.teamMinor} className={MONEY} />
      </button>
    </li>
  );
}

/** The expenses listed one by one, and those paid ahead: a bill with no amount always lists, so it can be entered. */
function splitCosts(costs: PositionCost[]): { listed: PositionCost[]; ahead: PositionCost[] } {
  const isAhead = (cost: PositionCost) => cost.amountMinor < 0 && !cost.missing;
  return { listed: costs.filter((cost) => !isAhead(cost)), ahead: costs.filter(isAhead) };
}

export function PositionPanels({ position, onOpen }: { position: CashPosition; onOpen: (patch: ParamPatch) => void }) {
  const { listed, ahead } = splitCosts(position.costs);
  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        <WorkspacePanel
          title={ENTITY_CONFIG.client.plural}
          summary={position.clients.length}
          collapsed={false}
          className="h-full"
          bodyClassName="p-0"
          subheader={<Columns first="Work done not paid, and costs and ads to rebill" wide={["Team, for work done"]} />}
          footer={
            <TotalsFooter
              items={[
                total(position.clientsOweMinor, "Clients owe us", COMPOSITION.clientsAhead),
                { label: "Team, for work done to today", amountMinor: position.teamMinor },
              ]}
            />
          }
        >
          {position.clients.length === 0 ? (
            <Empty>{`No ${ENTITY_CONFIG.client.label.toLowerCase()} owes anything`}</Empty>
          ) : (
            <ul className="divide-y divide-gray-100">
              {position.clients.map((client) => (
                <ClientRow key={client.clientId} client={client} onOpen={onOpen} />
              ))}
            </ul>
          )}
        </WorkspacePanel>
        <WorkspacePanel
          title="People"
          summary={position.people.length}
          collapsed={false}
          className="h-full"
          bodyClassName="p-0"
          subheader={<Columns first="Owed for work done to today" />}
          footer={<TotalsFooter items={[{ label: "Owed for work done to today", amountMinor: position.teamMinor }]} />}
        >
          {position.people.length === 0 ? (
            <Empty>Nobody is owed anything</Empty>
          ) : (
            <ul className="divide-y divide-gray-100">
              {position.people.map((person) => (
                <PersonRow key={person.personId} person={person} onOpen={onOpen} />
              ))}
            </ul>
          )}
        </WorkspacePanel>
      </div>
      <WorkspacePanel
        title={COMPOSITION.costs}
        summary={position.costs.length}
        collapsed={false}
        bodyClassName="p-0"
        footer={<TotalsFooter items={[total(position.costsMinor, COMPOSITION.costs, COMPOSITION.costsAhead)]} />}
      >
        {position.costs.length === 0 ? (
          <Empty>Nothing run up and not paid</Empty>
        ) : (
          <ul aria-label={COMPOSITION.costs} className="divide-y divide-gray-100">
            {listed.map((cost) => (
              <li key={`${cost.kind}:${cost.expenseId}`}>
                <button type="button" onClick={() => onOpen(opensPatch(costOpens(cost)))} className={ROW}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900 truncate">{cost.label}</span>
                    <span className="block text-xs text-gray-500 truncate">{costNote(cost)}</span>
                  </span>
                  <MoneyText amountMinor={Math.abs(cost.amountMinor)} className={MONEY} />
                </button>
              </li>
            ))}
            {ahead.length > 0 && (
              <li className="flex items-center gap-3 px-4 py-2.5 text-gray-400">
                <span className="min-w-0 flex-1 text-sm truncate">{`Paid ahead on ${ahead.length} ${ahead.length === 1 ? "expense" : "expenses"}`}</span>
                <MoneyText amountMinor={position.costsAheadMinor} muted className={MONEY} />
              </li>
            )}
          </ul>
        )}
      </WorkspacePanel>
    </>
  );
}
